import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { BannersPlacement } from "@/components/BannersPlacement/BannersPlacement";
import { Button } from "@/components/Button";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { PromoCodeForm } from "@/components/PromoCodeForm/PromoCodeForm";
import { useFlightFares } from "@/features/flights/components/flight-fares/useFlightFares";
import { FlightDetailsDrawer } from "@/features/flights/components/flight-purchase/FlightDetailsDrawer";
import { FlightOrderSummary } from "@/features/flights/components/flight-purchase/FlightOrderSummary";
import { FlightsOverview } from "@/features/flights/components/flight-purchase/FlightsOverview";
import { PassengersAndServicesOverview } from "@/features/flights/components/flight-purchase/PassengersAndServicesOverview";
import { TravellerDetailsDrawer } from "@/features/flights/components/flight-purchase/TravellerDetailsDrawer";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { useSelectedDepartureFlight } from "@/features/flights/hooks/flightSearch/useSelectedDepartureFlight";
import { useSelectedReturnFlight } from "@/features/flights/hooks/flightSearch/useSelectedReturnFlight";
import { flightAvailabilityQueryOptions } from "@/features/flights/hooks/useFlightAvailability";
import type { FlightType } from "@/features/flights/types/types";
import { getFlightOrderPrices } from "@/features/flights/utils/getFlightOrderPrices";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { usePromoCodeValidation } from "@/hooks/usePromoCodeValidation";
import { PromoCodeTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import type { FindUserTravelersTravelers } from "@/types/codegen";
import { BannersPlacementSlug } from "@/types/enums";
import { formatCurrency } from "@/utils/currency";

export const Route = createFileRoute(
  "/_authenticated/flights/_flights/_flight-offer/offer/$id/step/3",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const intl = useIntl();
  const navigate = Route.useNavigate();

  const { selectedFlightPayload, promoCode, setPromoCode } =
    useFlightSelectionPayloadContext();
  const { data: singleFlightDetails } = useQuery(
    flightAvailabilityQueryOptions(selectedFlightPayload),
  );
  const { selectedTravellers, travellerSlots } =
    useTravellersSelectionContext();
  const { selectedFare } = useFlightFares();
  const { result: selectedDepartureFlight } = useSelectedDepartureFlight();
  const { result: selectedReturnFlight } = useSelectedReturnFlight();

  const [selectedFlight, setSelectedFlight] = useState<
    FlightType | undefined | null
  >(null);
  const [selectedTraveller, setSelectedTraveller] =
    useState<FindUserTravelersTravelers | null>(null);

  const arePromoCodesEnabled = useFeatureIsOn("promo-codes");
  const areAdditionalServicesEnabled = useFeatureIsOn(
    "additional-services-selection",
  );

  const { validationStatus: promoCodeValidationStatus, data: promoCodeData } =
    usePromoCodeValidation({
      service: "flights",
      validationRequestData: {
        promoCode: promoCode || "",
        availabilityToken: singleFlightDetails?.availabilityToken || "",
      },
      onCodeApplied: (promoCode, data) => {
        rudderAnalytics.track(
          PromoCodeTrackingEvents.USER_PROMO_CODE_APPLIED_SUCCESSFULLY,
          {
            promo_code: promoCode,
            discount_amount: data.discountAmount,
            flow_type: "flight",
          },
        );
      },
      onCodeFailure: (promoCode, errorMessage) => {
        rudderAnalytics.track(
          PromoCodeTrackingEvents.USER_PROMO_CODE_APPLICATION_FAILED,
          {
            promo_code: promoCode,
            flow_type: "flight",
            error_message: errorMessage,
          },
        );
      },
    });

  const baggageAllowancePerAdult = useMemo(
    () =>
      singleFlightDetails
        ? [
            ...singleFlightDetails.outboundBaggage.baggage,
            ...singleFlightDetails.outboundBaggage.carryOn,
          ].reduce((acc, bagagge) => acc + bagagge.numberOfPieces, 0) +
          ([
            ...(singleFlightDetails.returnBaggage?.baggage ?? []),
            ...(singleFlightDetails.returnBaggage?.carryOn ?? []),
          ].reduce((acc, bagagge) => acc + bagagge.numberOfPieces, 0) || 0)
        : 0,
    [singleFlightDetails],
  );

  if (!singleFlightDetails) return null;

  const {
    basePrice,
    baseTicketPrice,
    servicesPrice,
    farePrice,
    totalPrice,
    promoCodeDiscountAmount,
  } = getFlightOrderPrices(singleFlightDetails, selectedFare, promoCodeData);

  return (
    <>
      <div className="px-4">
        <div className="mb-4 text-xl font-medium text-text-title">
          {intl.formatMessage({
            defaultMessage: "Review the flight details",
            description: "Flight checkout page title",
          })}
        </div>

        <div className="flex flex-col gap-2">
          <FlightsOverview
            flights={singleFlightDetails.flights}
            onClick={(direction) => {
              if (direction === "departure") {
                setSelectedFlight(selectedDepartureFlight);
                return;
              }
              if (direction === "return") {
                setSelectedFlight(selectedReturnFlight);
                return;
              }

              setSelectedFlight(null);
            }}
          />

          {selectedTravellers.length > 0 ? (
            <PassengersAndServicesOverview
              selectedTravellers={selectedTravellers}
              travellerSlots={travellerSlots}
              baggageAllowancePerAdult={baggageAllowancePerAdult}
              onTravellerClick={setSelectedTraveller}
            />
          ) : null}

          <BannersPlacement
            slug={BannersPlacementSlug.CHECKOUT}
            classNames={{
              wrapper: "-mx-main",
            }}
          />

          {arePromoCodesEnabled ? (
            <PromoCodeForm
              appliedCode={promoCode || ""}
              validationStatus={promoCodeValidationStatus}
              onCodeApply={({ code }) => {
                rudderAnalytics.track(
                  PromoCodeTrackingEvents.USER_SUBMIT_PROMO_CODE,
                  { promo_code: code, flow_type: "flight" },
                );

                setPromoCode(code);
              }}
              onCodeRemove={({ code, isApplied }) => {
                if (isApplied) {
                  rudderAnalytics.track(
                    PromoCodeTrackingEvents.USER_REMOVE_PROMO_CODE,
                    {
                      promo_code: code,
                      discount_amount_removed: promoCodeData?.result
                        ? (promoCodeData.discountAmount ?? null)
                        : null,
                      flow_type: "flight",
                    },
                  );
                }

                setPromoCode(null);
              }}
              onFocus={(hasAppliedPromoCode) => {
                if (!hasAppliedPromoCode) {
                  rudderAnalytics.track(
                    PromoCodeTrackingEvents.USER_SELECTED_PROMO_CODE,
                    { flow_type: "flight" },
                  );
                }
              }}
            />
          ) : null}
          <FlightOrderSummary
            basePrice={baseTicketPrice}
            brandedFarePrice={farePrice === 0 ? undefined : farePrice}
            servicesPrice={
              areAdditionalServicesEnabled ? servicesPrice : undefined
            }
            promoCodeDiscount={
              arePromoCodesEnabled ? promoCodeDiscountAmount : undefined
            }
            totalPrice={totalPrice}
          />
        </div>

        <TravellerDetailsDrawer
          traveller={selectedTraveller}
          onClose={() => setSelectedTraveller(null)}
          flightDetails={singleFlightDetails}
        />

        <FlightDetailsDrawer
          flight={selectedFlight}
          onClose={() => {
            setSelectedFlight(null);
          }}
        />
      </div>
      <PersistentDrawer>
        <div className="flex h-full flex-col gap-4">
          <div className="flex items-center justify-between gap-4 text-text-primary">
            <div>
              <div className="text-sm font-semibold">
                {intl.formatMessage({
                  defaultMessage: "Total price",
                  description: "Label for the total booking price",
                })}
              </div>
              <div className="text-xs text-text-placeholder">
                {intl.formatMessage({
                  defaultMessage: "Include taxes and charges",
                  description: "Total booking price description",
                })}
              </div>
            </div>

            <div className="text-base font-semibold">
              {formatCurrency(basePrice)}
            </div>
          </div>

          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              navigate({
                to: "/flights/offer/$id/step/4",
              });
            }}
            disabled={promoCodeValidationStatus?.type === "loading"}
          >
            {intl.formatMessage({
              defaultMessage: "Continue to Payment",
              description:
                "Label for the button to proceed to the payment step after reviewing the booking summary",
            })}
          </Button>
        </div>
      </PersistentDrawer>
    </>
  );
}
